import { useState } from 'react';
import styled from 'styled-components';
import * as Sentry from '@sentry/react';

import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MuiDivider from '@mui/material/Divider';
import MuiIconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { NgButton } from '../UI/NgButton/NgButton';
import { buttonVariants, buttonWidths } from '@clatter/ui';

const MOODS = [
  { value: 'frustrated', emoji: '😤', label: 'Frustrated' },
  { value: 'okay', emoji: '😐', label: "It's okay" },
  { value: 'happy', emoji: '😊', label: 'Happy' },
  { value: 'love', emoji: '🥰', label: 'Love it' },
] as const;

type Mood = (typeof MOODS)[number]['value'];

const CATEGORIES = ['Bug', 'Suggestion', 'Compliment', 'Question'] as const;
type Category = (typeof CATEGORIES)[number];

const Wrap = styled.div`
  font-family: ${({ theme }) => theme.fontFamily?.main ?? 'inherit'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const Title = styled.h2`
  margin: 0 0 2px;
  font-size: ${({ theme }) => theme?.fontSize?.h4};
  font-weight: 700;
  color: ${({ theme }) => theme?.palette?.textStrong};
  font-family: inherit;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme?.fontSize?.sm ?? '0.75rem'};
  color: ${({ theme }) => theme?.palette?.textSubtle ?? '#718096'};
`;

const SectionLabel = styled.p`
  margin: 0 0 8px;
  font-size: ${({ theme }) => theme?.fontSize?.xs ?? '0.6875rem'};
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme?.palette?.textMuted ?? '#4B5563'};
`;

const OptionalTag = styled.span`
  font-size: ${({ theme }) => theme?.fontSize?.badge ?? '0.625rem'};
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: ${({ theme }) => theme?.palette?.textSubtle ?? '#718096'};
  margin-left: 5px;
`;

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.default};
`;

const MoodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing.small};
`;

const MoodCard = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  aspect-ratio: 1;
  padding: 6px;
  border-radius: ${({ theme }) => theme?.radius?.medium ?? '0.5rem'};
  border: 1.5px solid
    ${({ $selected, theme }) =>
      $selected ? theme?.palette?.primaryButton ?? '#0A6EFF' : theme?.palette?.stroke ?? '#E8ECEF'};
  background: ${({ $selected, theme }) =>
    $selected ? theme?.palette?.background ?? '#E5FAFF' : theme?.palette?.white ?? '#fff'};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  font-family: inherit;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme?.palette?.primaryButton ?? '#0A6EFF'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .emoji {
    font-size: 22px;
    line-height: 1;
  }

  .label {
    font-size: ${({ theme }) => theme?.fontSize?.xs ?? '0.6875rem'};
    color: ${({ $selected, theme }) =>
      $selected ? theme?.palette?.primaryButton ?? '#0A6EFF' : theme?.palette?.textMedium ?? '#374151'};
    font-weight: ${({ $selected }) => ($selected ? '700' : '400')};
    white-space: nowrap;
  }
`;

const FollowUpBox = styled.div`
  border: 1px solid ${({ theme }) => theme?.palette?.cardBorder ?? '#E5E7EB'};
  border-radius: ${({ theme }) => theme?.radius?.medium ?? '0.5rem'};
  padding: 10px 12px;
  background: ${({ theme }) => theme?.palette?.cardSurface ?? '#f1f5f9'};
  margin-bottom: ${({ theme }) => theme.spacing.default};
`;

const FollowUpTitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme?.fontSize?.body ?? '0.8125rem'};
  font-weight: 600;
  color: ${({ theme }) => theme?.palette?.textStrong ?? '#111827'};
`;


const ErrorText = styled.p`
  margin: 0 0 10px;
  font-size: ${({ theme }) => theme?.fontSize?.xs ?? '0.6875rem'};
  color: ${({ theme }) => theme?.palette?.error ?? '#fe615a'};
`;

const SuccessWrap = styled.div`
  padding: ${({ theme }) => theme.spacing.medium};
  text-align: center;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 6px;
  font-size: ${({ theme }) => theme?.fontSize?.body ?? '0.8125rem'};
  font-weight: 700;
  color: ${({ theme }) => theme?.palette?.textStrong ?? '#111827'};
  font-family: inherit;
`;

const SuccessBody = styled.p`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme?.fontSize?.sm ?? '0.75rem'};
  color: ${({ theme }) => theme?.palette?.textSubtle ?? '#718096'};
`;

const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '0.8125rem',
    fontFamily: 'inherit',
    borderRadius: '7px',
    '& fieldset': { borderColor: '#E5E7EB' },
    '&:hover fieldset': { borderColor: '#0A6EFF' },
    '&.Mui-focused fieldset': { borderColor: '#0A6EFF' },
  },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0A6EFF' },
};

const checkboxSx = {
  color: '#E5E7EB',
  '&.Mui-checked': { color: '#0A6EFF' },
  pt: 0,
  pb: 0,
  pr: 0.75,
};

type Status = 'idle' | 'sending' | 'sent' | 'error';

export function FeedbackForm({ onClose }: { onClose?: () => void }) {
  const [mood, setMood] = useState<Mood | null>(null);
  const [category, setCategory] = useState<Category | null>(null);
  const [message, setMessage] = useState('');
  const [canFollowUp, setCanFollowUp] = useState(true);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!mood && !message.trim()) {
      setError("Please select how you're feeling or describe your feedback.");
      return;
    }

    setStatus('sending');
    setError(null);

    const moodLabel = MOODS.find((m) => m.value === mood)?.label;

    try {
      const client = Sentry.getClient();
      console.log('[FeedbackForm] Sentry client initialized:', !!client);
      if (!client) {
        console.warn(
          '[FeedbackForm] Sentry is NOT initialized — feedback will be silently dropped. Check NX_PUBLIC_SENTRY_DSN.',
        );
      } else {
        console.log('[FeedbackForm] Sentry DSN:', client.getDsn()?.host ?? '(none)');
      }

      const eventId = Sentry.captureFeedback(
        {
          message: message.trim() || '(no message)',
        },
        {
          captureContext: {
            tags: {
              'feedback.can_followup': 'true',
              'feedback.category': category || 'none',
              'feedback.mood': moodLabel || 'none',
            },
          },
        },
      );

      console.log('[FeedbackForm] captureFeedback returned eventId:', eventId);

      const flushed = await Sentry.flush(2000);
      console.log('[FeedbackForm] Sentry.flush completed, all events sent:', flushed);

      setStatus('sent');
    } catch (err) {
      console.error('[FeedbackForm] Failed to send feedback:', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  }

  if (status === 'sent') {
    return (
      <SuccessWrap>
        <SuccessTitle>Thanks — we got it.</SuccessTitle>
        <SuccessBody>Your feedback helps us improve.</SuccessBody>
        {onClose && (
          <NgButton variant={buttonVariants.primary} onClick={onClose}>
            Close
          </NgButton>
        )}
      </SuccessWrap>
    );
  }

  const isSending = status === 'sending';

  return (
    <Wrap as="form" onSubmit={handleSubmit} noValidate>
      {/* Header */}
      <Header>
        <div>
          <Title>Share feedback</Title>
          <Subtitle>Powered by Sentry</Subtitle>
        </div>
        {onClose && (
          <MuiIconButton size="small" onClick={onClose} aria-label="Close" sx={{ mt: '-2px' }}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </MuiIconButton>
        )}
      </Header>

      <MuiDivider sx={{ mb: 2 }} />

      {/* Mood */}
      <Section>
        <SectionLabel>How are you feeling?</SectionLabel>
        <MoodGrid>
          {MOODS.map(({ value, emoji, label }) => (
            <MoodCard
              key={value}
              type="button"
              $selected={mood === value}
              onClick={() => setMood((prev) => (prev === value ? null : value))}
              disabled={isSending}
            >
              <span className="emoji">{emoji}</span>
              <span className="label">{label}</span>
            </MoodCard>
          ))}
        </MoodGrid>
      </Section>

      {/* Category */}
      <Section>
        <SectionLabel>
          Category <OptionalTag>optional</OptionalTag>
        </SectionLabel>
        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={(_, val) => setCategory(val)}
          disabled={isSending}
          sx={{
            flexWrap: 'wrap',
            gap: '6px',
            '& .MuiToggleButtonGroup-grouped': {
              border: '1px solid #E5E7EB !important',
              borderRadius: '10px !important',
              margin: 0,
            },
          }}
        >
          {CATEGORIES.map((cat) => (
            <ToggleButton
              key={cat}
              value={cat}
              sx={{
                px: 1.25,
                py: 0.375,
                fontSize: '0.75rem',
                fontWeight: 500,
                fontFamily: 'inherit',
                textTransform: 'none',
                color: '#374151',
                '&.Mui-selected': {
                  color: '#0A6EFF',
                  backgroundColor: '#E5FAFF',
                  borderColor: '#0A6EFF !important',
                  fontWeight: 700,
                },
                '&.Mui-selected:hover': { backgroundColor: '#cff6ff' },
                '&:hover': { backgroundColor: 'rgba(227,227,227,0.6)' },
              }}
            >
              {cat}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Section>

      {/* Message */}
      <Section>
        <SectionLabel>
          What's on your mind? <OptionalTag>optional</OptionalTag>
        </SectionLabel>
        <TextField
          multiline
          minRows={4}
          fullWidth
          placeholder="Tell us anything — bugs, ideas, things you love or wish worked differently…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSending}
          sx={inputSx}
        />
      </Section>

      {/* Follow-up */}
      <FollowUpBox>
        <FormControlLabel
          control={
            <Checkbox
              checked={canFollowUp}
              onChange={(e) => setCanFollowUp(e.target.checked)}
              disabled={isSending}
              size="small"
              sx={checkboxSx}
            />
          }
          label={
            <div>
              <FollowUpTitle>I’m open to a follow-up conversation about this feedback.</FollowUpTitle>
            </div>
          }
          sx={{ alignItems: 'flex-start', mr: 0 }}
        />
      </FollowUpBox>

      {/* Error */}
      {error && <ErrorText role="alert">{error}</ErrorText>}

      {/* Submit */}
      <NgButton
        type="submit"
        variant={buttonVariants.primary}
        width={buttonWidths.full}
        isLoading={isSending}
        disabled={isSending}
      >
        Submit feedback
      </NgButton>
    </Wrap>
  );
}
